import React from 'react';
import { ChartNoAxesCombined, ShieldAlert, SearchCheck, Bug, FileChartColumnIncreasing } from 'lucide-react';
import UpgradePage from 'src/shared/templates/upgrade-page/UpgradePage';
import { ProductKey } from 'src/shared/types/engine/products';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import useDashboardAnalytics from 'src/app/dashboard/hooks/useDashboardAnalytics';
import dashboardUpgradeHero from 'src/assets/images/dashboard/upgrade/dashboard-upgrade-hero.webp';

/**
 * Shares Species Insights' HubSpot form: the Dashboard's metrics are entirely eDNA/species-derived,
 * and the original Figma copy for this page read "Upgrade to Species Insights today to unlock
 * the Nature Performance Dashboard...", confirming Dashboard is bundled under the same upgrade.
 */
const DASHBOARD_HUBSPOT_FORM_ID = '51e7950f-1853-4c81-98b3-c283da587706';

const FEATURES = [
    {
        icon: ChartNoAxesCombined,
        title: 'Track Key Biodiversity Trends',
        body: 'Monitor core metrics, including Species Richness and Evolutionary Diversity, over time to visualise the direct impact of your restoration or conservation efforts.',
    },
    {
        icon: ShieldAlert,
        title: 'Automated Risk & Conservation Screening',
        body: 'Instantly identify IUCN Red List and invasive species detected within your project, ensuring you stay ahead of regulatory risks and conservation priorities.',
    },
    {
        icon: SearchCheck,
        title: 'Deep-Dive Functional Indicators',
        body: 'Access specialised data on Soil Fungal and Bacterial Functional Diversity, alongside Marine Fish metrics, to understand the hidden drivers of ecosystem productivity and health.',
    },
    {
        icon: Bug,
        title: 'Notable Species Highlights',
        body: 'Receive detailed information on high-impact species detected at your sites, providing powerful, human-centric narratives for stakeholder communications and board-level reporting.',
    },
    {
        icon: FileChartColumnIncreasing,
        title: 'Audit-Ready Reporting',
        body: 'Leverage standardised, site-based metrics designed to align with global frameworks like TNFD and CSRD, turning complex ecological data into actionable business intelligence.',
    },
];

const FreemiumDashboard = () => {
    const breadcrumbs = useProjectBreadcrumb('Dashboard');
    const { trackUpgradeSubscriptionClick } = useDashboardAnalytics();

    return (
        <UpgradePage
            product={ProductKey.SPECIES_INSIGHTS}
            testId='dashboard-upgrade-page'
            breadcrumbs={breadcrumbs}
            heroImage={dashboardUpgradeHero}
            badgeLabel='Nature Performance Dashboard'
            title={
                <>
                    Visualise Impact,
                    <br />
                    Not Just Data
                </>
            }
            description='Move beyond static species lists to a dynamic view of ecosystem health. Upgrading to the Nature Performance Dashboard allows you to track biodiversity trends over time, transforming raw eDNA results into the clear, auditable narratives required for modern nature reporting.'
            features={FEATURES}
            ctaTitle='Ready to see your impact in real-time?'
            ctaDescription='Upgrade to unlock the Nature Performance Dashboard and benchmark ecological health across your entire portfolio.'
            hubSpotForm={{ formId: DASHBOARD_HUBSPOT_FORM_ID }}
            onBookCallClick={trackUpgradeSubscriptionClick}
        />
    );
};

export default FreemiumDashboard;
